"use client";

import { useDict } from "@/components/i18n/DictProvider";
import { CalendarDays, Clock, MapPin, Radio } from "lucide-react";
import { Container } from "@/components/ui/Container";
import { Breadcrumb } from "@/components/ui/Breadcrumb";
import { Badge } from "@/components/ui/Badge";
import { DetailHeroBanner } from "@/components/modules/DetailHeroBanner";
import { MetaPill } from "@/components/modules/MetaPill";
import { type Lang } from "@/lib/i18n";
import { detailCrumbs } from "@/lib/routes/detailCrumbs";
import { formatDate, formatItems } from "@/lib/format";
import { parsePaths } from "./cards/CardShell";
import { moduleBanner, moduleBannerAlt } from "@/lib/lecturehub/poster";
import type { Module } from "@/lib/lecturehub/types";

export function EventHero({
  lang,
  module: m,
  library = false,
  overlayHeader = false,
  cfHash = null,
}: {
  lang: Lang;
  module: Module;
  library?: boolean;
  /** Guest/public surface: header overlays the banner → add clearance padding. */
  overlayHeader?: boolean;
  /** Portal.cloudflareAccountHash — without it the hero falls back to the raw
   *  asset `uri` instead of the optimized CF `xl` variant. */
  cfHash?: string | null;
}) {
  const t = useDict();
  const ev = m.event;
  const live = ev?.eventType === "live";
  const paths = parsePaths(m.keywords);

  const location =
    ev?.city && ev?.country ? `${ev.city}, ${ev.country}` : (ev?.city ?? ev?.country ?? null);

  return (
    <>
      <DetailHeroBanner
        imageUrl={moduleBanner(m, cfHash)}
        imageAlt={moduleBannerAlt(m, m.name)}
        overlayHeader={overlayHeader}
      >
        <div className="mb-4 flex flex-wrap items-center gap-2">
          <Badge tone="gold">{t.detail.typeEvent}</Badge>
          {live ? (
            <span className="inline-flex items-center gap-1.5 rounded-full bg-destructive px-2.5 py-1 text-[11px] font-bold uppercase tracking-wide text-white">
              <Radio className="size-3 animate-pulse" aria-hidden />
              {t.detail.live}
            </span>
          ) : null}
        </div>
        <h1 className="max-w-4xl !text-[28px] !font-semibold !leading-[1.08] !tracking-tight !text-white md:!text-[44px]">
          {m.name}
        </h1>
        {/* Abstract intentionally NOT rendered here — moved inside the body
            EventView "About" card as an editorial deck. */}
        <div className="mt-6 flex flex-wrap gap-x-5 gap-y-2 text-[13px] text-white/85">
          {ev?.dateFrom ? (
            <MetaPill Icon={CalendarDays}>
              {formatDate(ev.dateFrom, lang)}
              {ev.dateTo && ev.dateTo !== ev.dateFrom ? ` — ${formatDate(ev.dateTo, lang)}` : ""}
            </MetaPill>
          ) : null}
          {location ? <MetaPill Icon={MapPin}>{location}</MetaPill> : null}
          {ev?.items?.length ? (
            <MetaPill Icon={Clock}>{formatItems(ev.items.length, lang, t.units)}</MetaPill>
          ) : null}
        </div>
        {paths.length > 0 ? (
          <div className="mt-5 flex flex-wrap gap-1.5">
            {paths.slice(0, 6).map((p) => (
              <span
                key={p}
                className="rounded-full bg-white/10 px-2 py-0.5 text-[10px] font-bold uppercase tracking-[0.14em] text-white/75"
              >
                {p}
              </span>
            ))}
          </div>
        ) : null}
      </DetailHeroBanner>
      <section>
        <Container className="pt-5">
          <Breadcrumb items={detailCrumbs(t, lang, "events", m.name, library)} />
        </Container>
      </section>
    </>
  );
}
